import { RenderingType } from '../../../../product-mvc-buttons/components/enums';
import type { ProductVariationValue } from '../../../types/product-card';
import type { ProductListVariation } from '../types/types';

type Swatches = Array<ProductVariationValue & { key: string }>;

export interface GroupedVariation {
  title: string;
  count: number;
}

const VISIBLE_SWATCHES_COUNT = 5;
const VISIBLE_SWATCHES_COUNT_SMALL = 4;

const filterAvailableSwatches = (allSwatches: Swatches, isSmallerScreen?: boolean) => {
  const swatches = allSwatches
    .map(({ selected, ...value }) => value)
    .filter(({ sku }) => sku !== null);

  const swatchVisibilityCondition = !isSmallerScreen
    ? swatches.length > VISIBLE_SWATCHES_COUNT
    : swatches.length > VISIBLE_SWATCHES_COUNT_SMALL;

  const swatchCount = isSmallerScreen ? VISIBLE_SWATCHES_COUNT_SMALL : VISIBLE_SWATCHES_COUNT;

  let visibleSwatchesCount = swatchVisibilityCondition ? swatchCount : swatches.length;

  const extraSwatchesCount = swatches.length - visibleSwatchesCount;

  const isExtraSwatchesDoubleDigits = extraSwatchesCount > 9;

  if (
    isSmallerScreen &&
    isExtraSwatchesDoubleDigits &&
    visibleSwatchesCount >= VISIBLE_SWATCHES_COUNT_SMALL
  ) {
    visibleSwatchesCount = visibleSwatchesCount - 1;
  }

  return {
    swatches,
    allSwatchesCount: allSwatches.length,
    visibleSwatchesCount,
  };
};

export const getSwatchesFromVariations = (
  variations: ProductListVariation[],
  isSmallerScreen?: boolean
) => {
  const iconVariations = variations?.filter(
    ({ renderingType }) => renderingType === RenderingType.Icon
  );
  if (!iconVariations || iconVariations.length === 0) {
    return { swatches: [], allSwatchesCount: 0, visibleSwatchesCount: 0 };
  }
  const allSwatches = iconVariations.reduce<Swatches>(
    (accVariations, variation) => [
      ...accVariations,
      ...variation.groups.reduce<Swatches>(
        (accValues, group) => [
          ...accValues,
          ...group.values.map(value => ({
            ...value,
            key: variation.key,
          })),
        ],
        []
      ),
    ],
    []
  );

  // Move selected variation to the beginning of an array
  const selectedIndex = allSwatches.findIndex(({ selected }) => selected);
  allSwatches.unshift(allSwatches.splice(selectedIndex, 1)[0]);

  return filterAvailableSwatches(allSwatches, isSmallerScreen);
};

export const groupOtherVariations = (variations: ProductListVariation[]) =>
  variations?.reduce((accVariations: GroupedVariation[], variation: ProductListVariation) => {
    if (variation.renderingType === RenderingType.Icon) {
      return accVariations;
    }

    return [
      ...accVariations,
      {
        title: variation.title,
        count: variation.groups.reduce((accGroups, group) => accGroups + group.values.length, 0),
      },
    ];
  }, []) ?? [];

export const getVariationTitle = (variations: ProductListVariation[]) => {
  const [variation] = variations.filter(v => v.renderingType === RenderingType.Icon);

  return variation?.title;
};
